import { useTranslation } from '@silospot/i18n/react';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Btn, Card, PageHeader } from '../../components/cld';
import { BlocklistModal } from '../../components/cld/blocklist-modal';
import { api } from '../../lib/api';
import { fmtDate } from '../../lib/datetime';

export function BlocklistPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['cld-blocklist', id],
    queryFn: () => api.getBlocklist(id),
    enabled: !!id,
  });
  const remove = useMutation({
    mutationFn: (entryId: number) => api.removeBlocklist(id, entryId),
    onSuccess: () => {
      toast.success(t('blocklist.removed'));
      void qc.invalidateQueries({ queryKey: ['cld-blocklist', id] });
    },
  });

  const header = (
    <PageHeader
      title={t('blocklist.title')}
      sub={t('blocklist.subActive')}
      actions={
        <Btn primary onClick={() => setAdding(true)}>
          {t('blocklist.add')}
        </Btn>
      }
    />
  );

  return (
    <div>
      {header}
      <Card className="p-0">
        {isLoading || !data ? (
          <div className="h-48 animate-pulse bg-neutral-bg" />
        ) : data.entries.length === 0 ? (
          <p className="py-12 text-center text-[13px] text-text-3">{t('blocklist.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  {[
                    t('blocklist.ip'),
                    t('blocklist.action'),
                    t('blocklist.scope'),
                    t('blocklist.reason'),
                    t('blocklist.date'),
                    t('blocklist.expires'),
                    '',
                  ].map((h, i) => (
                    <th
                      key={h || i}
                      className="whitespace-nowrap border-b border-border px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="hover:bg-[#fafbfc]">
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-3 font-mono text-text">
                      {e.ip}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-3">
                      <Badge tone={e.action === 'challenge' ? 'suspect' : 'fraud'}>
                        {e.action === 'challenge' ? t('blocklist.challenge') : t('blocklist.block')}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-3 text-text-2">
                      {e.scope === 'site' ? t('blocklist.scopeSite') : t('blocklist.scopeAll')}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-3 text-text-2">
                      {e.reason || '—'}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-3 text-text-3">
                      {fmtDate(e.createdAt)}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-3 text-text-3">
                      {e.expiresAt ? fmtDate(e.expiresAt) : t('blocklist.ttlPermanent')}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-3 text-right">
                      <button
                        type="button"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(e.id)}
                        className="text-[12.5px] font-semibold text-accent-strong hover:underline disabled:opacity-50"
                      >
                        {t('blocklist.unblock')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {adding ? (
        <BlocklistModal projectId={id} prefill={{}} onClose={() => setAdding(false)} />
      ) : null}
    </div>
  );
}
